import { type FormEvent, useEffect, useState } from "react";

import { changeRequiredPassword, getCurrentAdmin, logoutAdmin } from "./api";
import { useAuthCopy } from "./authCatalog";

export function RequiredPasswordChangePage({
  onComplete,
  onSessionUnavailable,
}: {
  onComplete: () => void;
  onSessionUnavailable: () => void;
}) {
  const { t } = useAuthCopy();
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void getCurrentAdmin()
      .then((actor) => {
        if (!active) return;
        if (!actor.requires_password_change) onComplete();
        else setChecking(false);
      })
      .catch(() => active && onSessionUnavailable());
    return () => { active = false; };
  }, [onComplete, onSessionUnavailable]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const currentPassword = String(data.get("current_password") ?? "");
    const newPassword = String(data.get("new_password") ?? "");
    const confirmation = String(data.get("confirmation") ?? "");
    if (newPassword !== confirmation) {
      setError(t("passwordChange.mismatch"));
      return;
    }
    if (currentPassword === newPassword) {
      setError(t("passwordChange.same"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await changeRequiredPassword({ currentPassword, newPassword });
      form.reset();
      onComplete();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("passwordChange.error"));
      setSubmitting(false);
    }
  }

  if (checking) return <main className="admin-route-state">{t("passwordChange.checking")}</main>;

  return (
    <main className="password-change-page">
      <section className="password-change-panel" aria-labelledby="password-change-title">
        <div className="admin-brand compact"><span>K</span><strong>Kavoosh</strong></div>
        <p className="admin-eyebrow">{t("passwordChange.eyebrow")}</p>
        <h1 id="password-change-title">{t("passwordChange.title")}</h1>
        <p>{t("passwordChange.body")}</p>
        <form onSubmit={submit}>
          <label>{t("passwordChange.current")}<input name="current_password" type="password" autoComplete="current-password" required /></label>
          <label>{t("passwordChange.new")}<input name="new_password" type="password" autoComplete="new-password" minLength={14} maxLength={1024} required /></label>
          <label>{t("passwordChange.confirm")}<input name="confirmation" type="password" autoComplete="new-password" minLength={14} maxLength={1024} required /></label>
          {error && <p className="admin-form-error" role="alert">{error}</p>}
          <button className="admin-primary" disabled={submitting}>{submitting ? t("passwordChange.replacing") : t("passwordChange.replace")}</button>
        </form>
        <button className="admin-text-button" onClick={() => void logoutAdmin().finally(onSessionUnavailable)}>{t("passwordChange.signOut")}</button>
      </section>
    </main>
  );
}
